import { DoNotDisturb, MailOutline, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    resetConditionalSplitSend,
    setSplitAction,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, SplitActions, SplitTypes } from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import useQueryParams from 'hooks/useQueryParams';
import FormLayout from 'domains/core/components/FormLayout';
import DisabledTooltipContent from '../../../components/DisabledTooltipContent';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseSplitAction = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const activeSend = campaignState.sends[activeSendIndex];
    const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;
    const isMeetsCriteriaSplit = isActiveSendEmail && activeSend.splitType === SplitTypes.MEETS;
    const isDoesNotMeetCriteriaSplit = isActiveSendEmail && activeSend.splitType === SplitTypes.DOES_NOT_MEET;

    // Check if editing a paused campaign
    const isEditingPausedCampaign = hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;
    // Get existing split action for the current send
    const existingSplitAction = isActiveSendEmail ? (activeSend as any)?.splitAction : null;

    const handleExitClick = () => {
        setSelectedAction(null);
        dispatchCampaignAction(resetConditionalSplitSend(activeSendIndex));
        dispatchCampaignAction(setSplitAction(activeSendIndex, SplitActions.EXIT));
    };

    // Handles what happens on split action selection
    useEffect(() => {
        const isExitClicked = selectedAction === CustomCampaignSteps.PUBLISH_CAMPAIGN;
        if (isMeetsCriteriaSplit && isExitClicked) {
            handleExitClick();
            setActiveSendIndex(activeSendIndex + 1);
        }
        if (isDoesNotMeetCriteriaSplit && isExitClicked) {
            handleExitClick();
            setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        }

        const isEmailClicked = selectedAction === CustomCampaignSteps.SELECT_CONTENT;
        if (isEmailClicked) {
            dispatchCampaignAction(setSplitAction(activeSendIndex, SplitActions.SEND));
            setActiveStep(selectedAction);
        }

        // We only want this to run when the send index or selected step change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, selectedAction]);

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedAction((event.target as HTMLInputElement).value);
    };

    // Determine which options should be disabled for paused campaigns
    const getIsOptionDisabled = (stepValue: CustomCampaignSteps) => {
        if (!isEditingPausedCampaign || !existingSplitAction) {
            return false;
        }
        // If email was selected (SEND), disable exit option
        if (existingSplitAction === SplitActions.SEND && stepValue === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
            return true;
        }
        // If exit was selected (EXIT), disable email option
        if (existingSplitAction === SplitActions.EXIT && stepValue === CustomCampaignSteps.SELECT_CONTENT) {
            return true;
        }
        return false;
    };

    const steps = [
        {
            disabled: getIsOptionDisabled(CustomCampaignSteps.SELECT_CONTENT),
            icon: <MailOutline sx={iconStyles} />,
            label: 'email',
            value: CustomCampaignSteps.SELECT_CONTENT,
        },
        {
            disabled: getIsOptionDisabled(CustomCampaignSteps.PUBLISH_CAMPAIGN),
            icon: <DoNotDisturb sx={iconStyles} />,
            label: 'exit curation',
            value: CustomCampaignSteps.PUBLISH_CAMPAIGN,
        },
    ];

    const guidingText = `Choose the next step for your audience who ${
        isDoesNotMeetCriteriaSplit ? 'does not meet' : 'meets'
    } the split criteria. Keeping your Curation goals in mind, select the action that supports your objective.`;

    return (
        <FormLayout guidingText={guidingText} header="select path actions" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleStepClick} value={selectedAction}>
                {steps.map((step) => (
                    <Tooltip key={step.value} title={step.disabled ? <DisabledTooltipContent /> : ''}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{
                                ...sharedButtonStyles,
                                ...(step.disabled && disabledButtonStyles),
                            }}
                            value={step.value}
                        />
                    </Tooltip>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseSplitAction;
